import type { Logger } from "@inference-net/cf-compatible-models";
import z from "zod";

import { UserRole } from "../const/roles";
import { ApiError } from "../errors/errors.classes";

export enum StatusCodes {
  UNKNOWN_CODE = "0",
  OK = "200",
  BAD_REQUEST = "400",
  UNAUTHORIZED = "401",
  PAYMENT_REQUIRED = "402",
  FORBIDDEN = "403",
  NOT_FOUND = "404",
  PAYLOAD_TOO_LARGE = "413",
  UNPROCESSABLE_ENTITY = "422",
  TOO_MANY_REQUESTS = "429",
  INTERNAL_SERVER_ERROR = "500",
  SERVICE_UNAVAILABLE = "503",
  GATEWAY_TIMEOUT = "504",
}

export enum NumberStatusCodes {
  UNKNOWN_CODE = 0,
  OK = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  PAYMENT_REQUIRED = 402,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  PAYLOAD_TOO_LARGE = 413,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

/** ******************************************************************************
 * Error Handling
 ******************************************************************************* */

export const serviceError = z.object({
  message: z.string(),
  type: z.string().default("ApiError"),
});

export type ServiceError = z.infer<typeof serviceError>;

export const toError = (error: unknown): ServiceError => {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      type: error.type,
    };
  }
  if (error instanceof Error) {
    return {
      message: error.message,
      type: error.name,
    };
  } else if (typeof error === "string") {
    return { message: error, type: "UnknownError" };
  } else if (
    error instanceof Object &&
    Object.keys(error).includes("message")
  ) {
    return {
      message: (
        error as {
          message: string;
        }
      ).message,
      type: (error as { type: string | undefined }).type ?? "UnknownError",
    };
  } else {
    return {
      message: "Unknown error",
      type: "UnknownError",
    };
  }
};

/** ******************************************************************************
 *  Authentication
 ******************************************************************************* */

export const authToken = z.string();

/** How the caller reached the team it is acting on. */
export const teamAccessModeSchema = z.enum([
  "member",
  "superadmin-readonly",
  "superadmin-write",
  "demo-readonly",
]);

export type AuthTeamAccessMode = z.infer<typeof teamAccessModeSchema>;

export const authData = z.object({
  email: z.string().optional(),
  userId: z.string().optional(),
  teamId: z.string().optional(),
  projectId: z.string().optional(),
  token: authToken.optional(),
  apiKey: z.string().optional(),
  apiKeyId: z.string().optional(),
  apiKeyPermissions: z.array(z.enum(["read", "write"])).optional(),
  role: z.nativeEnum(UserRole).nullable().optional(),
  /** Better Auth `user.emailVerified` — required true for super-admin gates. */
  emailVerified: z.boolean().optional(),
  teamAccessMode: teamAccessModeSchema.optional(),
});

export type AuthToken = z.infer<typeof authToken>;
export type AuthData = z.infer<typeof authData>;

/** ******************************************************************************
 *  Request/Response
 ******************************************************************************* */

export const serviceRequest = z.object({});

export const authedServiceRequest = z.object({
  auth: authData,
});

const statuses = Object.keys(StatusCodes) as StatusCodes[];

export const serviceResponse = z.object({
  status: z.enum([statuses[0] ?? "", ...statuses]),
  error: serviceError.optional(),
});

export type ServiceRequest = z.infer<typeof serviceRequest>;
export type AuthedServiceRequest = z.infer<typeof authedServiceRequest>;
export type ServiceResponse = z.infer<typeof serviceResponse>;

/** ******************************************************************************
 *  Signing Service Request/Response
 ******************************************************************************* */

export const signingServiceRequest = z.object({
  auth: authData,
});

export const signingServiceBaseParams = z.object({
  adminKey: z.string(),
});

export const signingServiceResponse = serviceResponse.merge(
  z.object({
    signature: z.string().nullable(),
  }),
);

export type SigningServiceRequest = z.infer<typeof signingServiceRequest>;
export type SigningServiceBaseParams = z.infer<typeof signingServiceBaseParams>;
export type SigningServiceResponse = z.infer<typeof signingServiceResponse>;

/** ******************************************************************************
 *  BaseService Interface
 ******************************************************************************* */

export type BaseServiceParams = {
  logger: Logger;
};

export class BaseService {
  protected logger: Logger;

  constructor(params: BaseServiceParams) {
    this.logger = params.logger;
  }

  async init(): Promise<void> {
    return Promise.resolve();
  }

  async shutdown(): Promise<void> {
    return Promise.resolve();
  }
}
